/**
 * CFO PnL Live View — Sprint B
 *
 * Single-page CFO command center listing every project with its full
 * PnL pulled live from /api/reports/projects-pnl. Sorted by margin %
 * descending so the most-profitable projects bubble to the top.
 *
 * Columns:
 *   Project · Status · Revenue · Retention · Forecast Cost · GP · Margin%
 *   · Cash In · Cash Out · Net Cash
 *
 * Header bar shows tenant-wide totals + portfolio-level margin.
 */
import React, { useEffect, useState } from "react";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import {
  Loader2, TrendingUp, TrendingDown, DollarSign, Wallet,
  ArrowUpRight, ArrowDownRight, Briefcase,
} from "lucide-react";

const SAR = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency", currency: "SAR", maximumFractionDigits: 0,
  }).format(Number(n || 0));

const KPI = ({ label, value, sub, tone = "slate", icon: Icon, testid }) => {
  const tones = {
    slate:  "from-slate-50 to-white border-slate-200",
    green:  "from-emerald-50 to-white border-emerald-200",
    red:    "from-rose-50 to-white border-rose-200",
    blue:   "from-blue-50 to-white border-blue-200",
    amber:  "from-amber-50 to-white border-amber-200",
  };
  return (
    <div data-testid={testid}
          className={`rounded-xl border bg-gradient-to-br p-5 ${tones[tone]}`}>
      <div className="flex items-start justify-between">
        <div className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</div>
        {Icon && <Icon className="h-4 w-4 text-slate-400" />}
      </div>
      <div className="mt-2 text-2xl font-semibold text-slate-900">{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  );
};

const MarginPill = ({ pct }) => {
  const v = Number(pct || 0);
  const tone =
    v >= 15 ? "bg-emerald-100 text-emerald-700" :
    v >= 5  ? "bg-amber-100 text-amber-700" :
    v >= 0  ? "bg-slate-100 text-slate-700" :
              "bg-rose-100 text-rose-700";
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${tone}`}>
      {v.toFixed(1)}%
    </span>
  );
};

export default function CFOPnlPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (alive) setLoading(true);
      try {
        const r = await axiosInstance.get("/reports/projects-pnl?limit=200");
        if (alive) setData(r.data);
      } catch (e) {
        if (alive) toast.error("فشل تحميل لوحة الأرباح");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-20 text-slate-500">
      <Loader2 className="mr-2 h-6 w-6 animate-spin" /> جاري التحميل…
    </div>
  );
  if (!data) return null;
  const t = data.totals || {};
  const rows = data.rows || [];

  return (
    <div data-testid="cfo-pnl-page" className="space-y-6 p-6" dir="rtl">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">
          <Briefcase className="inline -mt-1 mr-2 h-7 w-7 text-indigo-600" />
          CFO PnL — Live Portfolio View
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          أرباح المحفظة في الوقت الحقيقي — Revenue · Cost · Profit · Cash لكل مشروع
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 lg:grid-cols-5" dir="ltr">
        <KPI label="Projects" value={t.projects || 0}
              tone="slate" icon={Briefcase} testid="pnl-projects" />
        <KPI label="Total Revenue" value={SAR(t.revenue)}
              sub={`Retention ${SAR(t.retention)}`}
              tone="blue" icon={TrendingUp} testid="pnl-revenue" />
        <KPI label="Forecast Cost" value={SAR(t.forecast_cost)}
              sub={`Actual ${SAR(t.actual_cost)} · Committed ${SAR(t.committed_cost)}`}
              tone="amber" icon={DollarSign} testid="pnl-cost" />
        <KPI label="Gross Profit" value={SAR(t.gross_profit)}
              sub={`Margin ${(t.margin_pct || 0).toFixed(1)}%`}
              tone={t.gross_profit >= 0 ? "green" : "red"}
              icon={t.gross_profit >= 0 ? TrendingUp : TrendingDown}
              testid="pnl-profit" />
        <KPI label="Net Cash Flow" value={SAR(t.net_cash)}
              sub={`In ${SAR(t.cash_in)} · Out ${SAR(t.cash_out)}`}
              tone={t.net_cash >= 0 ? "green" : "red"}
              icon={Wallet} testid="pnl-cash" />
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white" dir="ltr">
        <div className="border-b border-slate-200 bg-slate-50 px-5 py-3 text-sm font-semibold text-slate-700">
          Portfolio PnL ({rows.length} projects · sorted by margin)
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Project</th>
                <th className="px-3 py-2 text-left">Client</th>
                <th className="px-3 py-2 text-right">Revenue</th>
                <th className="px-3 py-2 text-right">Retention</th>
                <th className="px-3 py-2 text-right">Forecast Cost</th>
                <th className="px-3 py-2 text-right">GP</th>
                <th className="px-3 py-2 text-right">Margin</th>
                <th className="px-3 py-2 text-right">Cash In</th>
                <th className="px-3 py-2 text-right">Cash Out</th>
                <th className="px-3 py-2 text-right">Net Cash</th>
              </tr>
            </thead>
            <tbody data-testid="pnl-rows">
              {rows.map((r) => (
                <tr key={r.project_id} data-testid={`pnl-row-${r.project_id}`}
                    className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2 font-medium text-slate-900">{r.name}</td>
                  <td className="px-3 py-2 text-slate-500">{r.client_name}</td>
                  <td className="px-3 py-2 text-right">{SAR(r.revenue)}</td>
                  <td className="px-3 py-2 text-right text-amber-700">{SAR(r.retention)}</td>
                  <td className="px-3 py-2 text-right">{SAR(r.forecast_cost)}</td>
                  <td className={`px-3 py-2 text-right font-semibold ${r.gross_profit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                    {SAR(r.gross_profit)}
                  </td>
                  <td className="px-3 py-2 text-right"><MarginPill pct={r.margin_pct} /></td>
                  <td className="px-3 py-2 text-right text-emerald-700">{SAR(r.cash_in)}</td>
                  <td className="px-3 py-2 text-right text-rose-700">{SAR(r.cash_out)}</td>
                  <td className={`px-3 py-2 text-right font-semibold ${r.net_cash >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                    {SAR(r.net_cash)}
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-8 text-center text-slate-400">
                  لا توجد بيانات ربحية بعد.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
